import { Box, InputAdornment, Stack, TextField, Tooltip, Typography } from '@evaneos/b2b-ui';
import InfoOutlinedIcon from '@evaneos/b2b-ui/icons/InfoOutlined';
import ArrowForwardIcon from '@evaneos/b2b-ui/icons/ArrowForward';
import { useIntl } from 'react-intl';

import { ChangeEvent, useState } from 'react';

import { DefinitionList } from '../../../../design-system/DefinitionList/DefinitionList';
import { useGetDossierData } from '../../../../resources/dossier/queries';
import { DossierId } from '../../../../resources/dossier/types';
import { useLocale } from '../../../../shared/providers/LocalizationProvider';

import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../../shared/analytics';
import { useUpdateBudgetMutation } from '../../../../resources/dossier/mutations';

export const BudgetEditableItem = ({ dossierId }: { dossierId: DossierId }) => {
    const { formatMessage, formatNumber } = useIntl();
    const { locale } = useLocale();
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);
    const budgetMutation = useUpdateBudgetMutation(dossierId);
    const { dossier } = useGetDossierData(dossierId) || {};
    const { project, budget: adjustedBudget, contract } = dossier || {};
    const { idealBudget, maxTotalInEuro } = project || {};

    const initialBudgetValue = adjustedBudget || idealBudget?.totalInEuro || maxTotalInEuro || '';

    const [adjustedBudgetInput, setAdjustedBudgetInput] = useState<number | ''>(initialBudgetValue);

    const handleChange = (
        event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        _close: () => void,
    ) => {
        const budgetValue = event.target.value ? parseInt(event.target.value) : '';
        setAdjustedBudgetInput(budgetValue);
    };

    const handleBeforeClose = () => {
        if (!adjustedBudgetInput) {
            return true;
        }

        if (adjustedBudgetInput !== initialBudgetValue) {
            budgetMutation.mutate({ budget: adjustedBudgetInput });

            trackEvent((ampli, defaultProperties) => {
                ampli.budgetUpdated({
                    ...defaultProperties,
                    ...trackedTripProperties,
                    budget_value: adjustedBudgetInput,
                    last_budget_before_update:
                        initialBudgetValue !== '' ? initialBudgetValue : undefined,
                });
            });
        }

        return true;
    };

    const formatBudget = (amount: number) =>
        formatNumber(amount, {
            style: 'currency',
            minimumFractionDigits: 0,
            currency: 'EUR',
        });

    const getBudgetHelperText = (): string => {
        if (idealBudget?.totalInEuro) {
            return formatMessage(
                { id: 'dossier.information.principal.budget.ideal.description.ideal' },
                { idealBudget: formatBudget(idealBudget?.totalInEuro) },
            );
        }
        if (maxTotalInEuro) {
            return formatMessage(
                { id: 'dossier.information.principal.budget.ideal.description.max' },
                { idealBudget: formatBudget(maxTotalInEuro) },
            );
        }
        return formatMessage({
            id: 'dossier.information.principal.budget.ideal.description.empty',
        });
    };

    const isDisabled = contract?.state !== 'not-issued';

    return (
        <DefinitionList.EditableValue
            labelId="budget"
            popoverWidth={maxTotalInEuro ? 'trigger' : 'fit-content'}
            placement="below"
            disablePopoverMinHeight
            disabled={isDisabled}
            disabledTooltip={
                isDisabled
                    ? formatMessage({ id: 'dossier.information.disabled.contract_tooltip' })
                    : undefined
            }
            onBeforeClose={handleBeforeClose}
            editor={({ close }) => (
                <Stack direction="row" padding={1.5} gap={1}>
                    <Stack gap={1}>
                        <Stack direction="row" gap={1}>
                            <Typography variant="body2" color="text.secondary">
                                {formatMessage({
                                    id: idealBudget?.totalInEuro
                                        ? 'dossier.information.principal.budget.ideal.label.ideal'
                                        : 'dossier.information.principal.budget.ideal.label.default',
                                })}
                            </Typography>
                            <Tooltip
                                title={formatMessage({
                                    id: 'dossier.information.principal.budget.ideal.tooltip',
                                })}
                                arrow
                                placement="top"
                            >
                                <InfoOutlinedIcon sx={{ fontSize: '16px', cursor: 'pointer' }} />
                            </Tooltip>
                        </Stack>
                        <TextField
                            type="number"
                            autoFocus
                            id="outlined-adornment-ideal-amount"
                            placeholder={formatMessage({
                                id: 'dossier.information.principal.budget.ideal.placeholder',
                            })}
                            size="small"
                            sx={{
                                alignItems: 'start!important',
                                '.MuiFormHelperText-root': {
                                    marginLeft: 0,
                                },
                                width: maxTotalInEuro ? '160px' : undefined,
                            }}
                            value={adjustedBudgetInput}
                            slotProps={{
                                htmlInput: {
                                    'data-testid': 'information-ideal-budget',
                                    sx: {
                                        fontSize: '1.4rem',
                                    },
                                },
                                input: {
                                    endAdornment: locale !== 'en' && (
                                        <InputAdornment position="end">€</InputAdornment>
                                    ),
                                    startAdornment: locale === 'en' && (
                                        <InputAdornment position="start">€</InputAdornment>
                                    ),
                                },
                            }}
                            onChange={(event) => handleChange(event, close)}
                            helperText={getBudgetHelperText()}
                        />
                    </Stack>
                    {maxTotalInEuro && (
                        <>
                            <Box alignContent="center">
                                <ArrowForwardIcon />
                            </Box>
                            <Stack gap={1}>
                                <Stack direction="row" gap={1}>
                                    <Typography variant="body2" color="text.secondary">
                                        {formatMessage({
                                            id: 'dossier.information.principal.budget.max.label',
                                        })}
                                    </Typography>
                                    <Tooltip
                                        title={formatMessage({
                                            id: 'dossier.information.principal.budget.max.tooltip',
                                        })}
                                        arrow
                                        placement="top"
                                    >
                                        <InfoOutlinedIcon
                                            sx={{ fontSize: '16px', cursor: 'pointer' }}
                                        />
                                    </Tooltip>
                                </Stack>
                                <TextField
                                    size="small"
                                    data-testid="information-max-budget"
                                    value={formatBudget(maxTotalInEuro)}
                                    disabled
                                    slotProps={{
                                        htmlInput: {
                                            sx: {
                                                fontSize: '1.4rem',
                                            },
                                        },
                                    }}
                                />
                            </Stack>
                        </>
                    )}
                </Stack>
            )}
        >
            <Typography variant="body2">
                {adjustedBudgetInput ? (
                    formatBudget(adjustedBudgetInput)
                ) : !isDisabled ? (
                    <Typography variant="body2" component="span" color="textSecondary">
                        {formatMessage({
                            id: 'dossier.information.principal.budget.ideal.placeholder',
                        })}{' '}
                    </Typography>
                ) : (
                    '-'
                )}{' '}
                {maxTotalInEuro ? (
                    <>
                        <Typography variant="body2" component="span" color="text.secondary">
                            {formatMessage({
                                id: 'dossier.information.principal.budget.ideal_indicator',
                            })}{' '}
                            →
                        </Typography>{' '}
                        <Typography variant="body2" component="span">
                            {formatBudget(maxTotalInEuro)}
                        </Typography>{' '}
                        <Typography variant="body2" component="span" color="text.secondary">
                            {formatMessage({
                                id: 'dossier.information.principal.budget.max_indicator',
                            })}
                        </Typography>
                    </>
                ) : null}
            </Typography>
        </DefinitionList.EditableValue>
    );
};
